import React, {
  useContext,
  createContext,
  Dispatch,
  useState,
  useCallback,
} from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useImmerReducer } from "use-immer";

import { Session } from "../../Authentication";
import { ErrorPage } from "../../ErrorPage";
import { LoadingPage, useLoadingState } from "../../Loading";
import { useVenue } from "../../VenueContext";
import { useBillSplitQuery } from "../BillSplit/queries";
import { generateEmptyCart } from "../Cart/reducer";
import { createNextCart, fetchCart, updateCart } from "../Cart/requests";
import { Cart } from "../Cart/schema";
import { useCustomerTabQuery } from "../CustomerTab/queries";
import { fetchCustomerTab } from "../CustomerTab/requests";
import { CustomerTab } from "../CustomerTab/schema";
import { SessionActions, sessionReducer } from "./reducer";
import { getSession } from "./requests";
import { useBillSplitChannel } from "./useBillSplitChannel";

export interface SessionContextType {
  session: Session;
  dispatch: Dispatch<SessionActions>;
}
const generateEmptySession = (): Session => ({
  cart: generateEmptyCart({ venueId: "" }),
  pastCustomerTabIds: [],
  pastCartIds: [],
});

export const SessionContext = createContext<SessionContextType>({
  session: generateEmptySession(),
  dispatch: () => {},
});

export const useSession = (): Session => {
  const { session } = useContext(SessionContext);
  if (!session) {
    throw new Error("useSession must be used within a SessionContext");
  }
  return session;
};

export const useCurrentCartUuid = (): string => {
  const session = useSession();
  return session.cart.uuid;
};

export const useSessionDispatch = () => {
  const { dispatch } = useContext(SessionContext);
  const queryClient = useQueryClient();
  return useCallback(
    (value: SessionActions) => {
      if (
        value.action == "addCartItem" ||
        value.action == "removeCartItem" ||
        value.action == "updateCartItem"
      ) {
        queryClient.invalidateQueries({
          queryKey: ["cart"],
        });
      }
      dispatch(value);
    },
    [dispatch, queryClient],
  );
};

export const SessionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { t } = useTranslation("onsite");
  const venue = useVenue();
  const [session, dispatch] = useImmerReducer(
    sessionReducer,
    generateEmptySession(),
  );
  const [customerTabId, setCustomerTabId] = useState<string | undefined>();

  //Because we don't know if the customer already has an existing cart and they
  // could be scanning the qr code for a tab for the first time, we need to ensure that
  // the tab and the cart belong togethor
  const fetchCartWithCustomerTab = async (
    cartId: string,
    customerTab?: CustomerTab,
  ): Promise<Cart> => {
    const cart = cartId
      ? await fetchCart(cartId)
      : await createNextCart({ venue, customerTab });

    if (customerTab && cart.customerTabId !== customerTab.uuid) {
      return await updateCart({
        customerTabId: customerTab.uuid,
        uuid: cart.uuid,
      });
    }
    return cart;
  };

  const loadSession = async () => {
    const sessionResponse = await getSession();
    const { venueCartIds, venueCustomerTabIds } = sessionResponse;
    const cartId = venueCartIds[venue.uuid];
    const customerTabId = venueCustomerTabIds[venue.uuid];
    setCustomerTabId(customerTabId);

    const customerTab = customerTabId
      ? await fetchCustomerTab({ uuid: customerTabId })
      : undefined;

    const cart = await fetchCartWithCustomerTab(cartId, customerTab);

    dispatch({
      action: "initializeSession",
      payload: {
        customerTab,
        cart,
        pastCustomerTabIds: [],
        pastCartIds: [],
      },
    });
  };

  useCustomerTabQuery(customerTabId);
  const billSplitSessionQuery = useBillSplitQuery(customerTabId);
  useBillSplitChannel(billSplitSessionQuery?.data);
  const loadingState = useLoadingState(loadSession, []);
  return (
    <SessionContext.Provider value={{ session, dispatch }}>
      {loadingState == "LOADED" && children}
      {(loadingState == "UNLOADED" || loadingState == "LOADING") && (
        <LoadingPage />
      )}
      {loadingState == "ERROR" && (
        <ErrorPage
          actionMessage={t("errorPage.tryAgain")}
          errorMessage={t("errorPage.genericError")}
          onAction={() => {
            // Try reloading the page.
            // Brutal But best way to handle this for now.
            window.location.reload();
          }}
        />
      )}
    </SessionContext.Provider>
  );
};
